import { useState, useCallback, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Play } from 'lucide-react'
import type { Participante } from '../types/sorteos.types'

interface CircularRouletteProps {
  participantes: Participante[]
  onResult: (participante: Participante) => void
  disabled?: boolean
  children?: React.ReactNode
}

export function CircularRoulette({ participantes, onResult, disabled, children }: CircularRouletteProps) {
  const [isSpinning, setIsSpinning] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const wheelRef = useRef<HTMLDivElement>(null)

  const colors = [
    '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4',
    '#115e59', '#134e4a', '#0f766e', '#0d9488', '#14b8a6',
  ]

  const spin = useCallback(() => {
    if (isSpinning || disabled || participantes.length === 0) return

    setIsSpinning(true)
    setSelectedName(null)

    const segmentAngle = 360 / participantes.length
    // Elegir índice aleatorio y calcular ángulo para que caiga en ese segmento
    const targetIndex = Math.floor(Math.random() * participantes.length)
    // Ángulo que apunta al centro del segmento target (el indicador está arriba = 0°)
    const targetAngle = 360 - (targetIndex * segmentAngle + segmentAngle / 2)
    const spins = 5 + Math.floor(Math.random() * 3)
    const totalRotation = rotation + spins * 360 + targetAngle + (Math.random() * segmentAngle * 0.6 - segmentAngle * 0.3)

    setRotation(totalRotation)

    setTimeout(() => {
      setSelectedName(participantes[targetIndex].nombre)
      setIsSpinning(false)
      onResult(participantes[targetIndex])
    }, 4000)
  }, [isSpinning, disabled, participantes, rotation, onResult])

  useEffect(() => {
    return () => setIsSpinning(false)
  }, [])

  const segmentAngle = participantes.length > 0 ? 360 / participantes.length : 360
  // Ajustar tamaño de fuente según cantidad de participantes
  const fontSize = participantes.length <= 6 ? 3.5 : participantes.length <= 12 ? 2.8 : participantes.length <= 20 ? 2.2 : 1.6
  const maxChars = participantes.length <= 6 ? 12 : participantes.length <= 12 ? 10 : 8

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="text-lg">Ruleta</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center gap-4">
          {/* Wheel + Result */}
          <div className="relative">
            {/* Wheel */}
            <div className="relative w-72 h-72">
              {/* Indicator */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-l-transparent border-r-transparent border-t-teal-700" />

              <div
                ref={wheelRef}
                className="w-full h-full rounded-full border-4 border-teal-500 overflow-hidden relative"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                }}
              >
                {participantes.length === 0 ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full">
                    <p className="text-sm text-muted-foreground">Sin participantes</p>
                  </div>
                ) : (
                  participantes.map((p, i) => {
                    const startAngle = i * segmentAngle
                    const midAngle = startAngle + segmentAngle / 2
                    const radians = (midAngle - 90) * (Math.PI / 180)
                    const textX = 50 + 30 * Math.cos(radians)
                    const textY = 50 + 30 * Math.sin(radians)
                    const label = p.nombre.length > maxChars ? p.nombre.slice(0, maxChars) + '…' : p.nombre

                    return (
                      <div key={p.id}>
                        <svg
                          className="absolute inset-0 w-full h-full"
                          viewBox="0 0 100 100"
                        >
                          <path
                            d={describeArc(50, 50, 50, startAngle, startAngle + segmentAngle)}
                            fill={colors[i % colors.length]}
                            stroke="white"
                            strokeWidth="0.5"
                          />
                          <text
                            x={textX}
                            y={textY}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="white"
                            fontSize={fontSize}
                            fontWeight="bold"
                            transform={`rotate(${midAngle}, ${textX}, ${textY})`}
                          >
                            {label}
                          </text>
                        </svg>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {/* Result - absolute so it doesn't push anything */}
            {selectedName && (
              <div className="absolute -right-2 top-1/2 -translate-y-1/2 translate-x-full text-center p-3 rounded-lg bg-teal-500/10 border border-teal-500 ml-4 max-w-[180px]">
                <p className="text-sm text-muted-foreground">Ganador:</p>
                <p className="text-lg font-bold text-teal-700 dark:text-teal-300 truncate">
                  {selectedName.length > 15 ? selectedName.split(' ')[0] : selectedName}
                </p>
              </div>
            )}
          </div>

          {/* Spin Button */}
          <Button
            className="w-full max-w-sm bg-teal-800 hover:bg-teal-900 text-white"
            onClick={spin}
            disabled={isSpinning || disabled || participantes.length === 0}
          >
            <Play className="h-4 w-4 mr-2" />
            {isSpinning ? 'Girando...' : participantes.length === 0 ? 'Sin participantes' : 'Girar Ruleta'}
          </Button>

          {children && (
            <div className="w-full border-t border-gray-200 dark:border-gray-700 pt-4">
              {children}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, r, endAngle)
  const end = polarToCartesian(cx, cy, r, startAngle)
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  }
}
